import { onlyNumbersRegex, onlyTextRegex } from '@constants/general';
import FileInputV3 from '@efex-components/EfexDS/FileInput/FileInput';
import { Box, FormControl, Grid, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEffectOnce from '@main-hooks/useEffectOnce';
import { DocumentTypeProps } from './DocumentType';
import StakeHolderContext from '../../../context/StakeHolderContext';
import { icon_check_circle_green } from '@/assets';

type DirectionInfoProps = DocumentTypeProps;

const DirectionInfo = ({
  defaultValues,
  handleDropChangeFile,
}: DirectionInfoProps) => {
  const { t } = useTranslation();

  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const {
    setProofOfAddressDocument,
    proofOfAddressDocument,
    changeProofOfAddressDocument,
    deleteProofOfAddressDocument,
  } = useContext(StakeHolderContext);

  const [
    addressLine1Field,
    addressLine2Field,
    cityField,
    stateField,
    postCodeField,
  ] = watch(['addressLine1', 'addressLine2', 'city', 'state', 'postCode']);

  useEffectOnce(() => {
    setProofOfAddressDocument(defaultValues?.documents || []);
  });

  useEffect(() => {
    setValue('proofOfAddress', proofOfAddressDocument || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofOfAddressDocument]);

  return (
    <Box sx={{ marginTop: 2 }}>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Typography variant="subtitle2">{t(`onbStHlResidence`)}</Typography>
          <div className="flex w-full">
            <TextField
              autoComplete="off"
              type="text"
              className="custom-textfield w-[95%]"
              size="small"
              placeholder={t('onbStakeAddress1') || ''}
              error={!!errors.addressLine1}
              helperText={errors.addressLine1?.message as string}
              {...register('addressLine1', {
                maxLength: {
                  value: 100,
                  message: t('addressTooLong'),
                },
                pattern: {
                  value: /^[a-zA-Z0-9\s]+$/,
                  message: t('addressInvalidCharacters'),
                },
                validate: {
                  noLeadingSpace: (value: string): any =>
                    !value.startsWith(' ') || t('addressStartsWithSpace'),
                  noTrailingSpace: (value: string): any =>
                    !value.endsWith(' ') || t('addressEndsWithSpace'),
                  noConsecutiveSpaces: (value: string): any =>
                    !/\s\s/.test(value) || t('addressConsecutiveSpaces'),
                },
              })}
              inputProps={{
                maxLength: 100,
                pattern: '[a-zA-Z0-9\\s]+',
                onInput: (e: React.FormEvent<HTMLInputElement>) => {
                  const input = e.currentTarget;
                  input.value = input.value.replace(/[^a-zA-Z0-9\s]/g, '');
                },
              }}
            />

            {addressLine1Field?.length > 0 && !errors.addressLine1 && (
              <img src={icon_check_circle_green} alt="" className="w-[25px]" />
            )}
          </div>

          {errors && errors.addressLine1 && (
            <p className="text-orange-600 text-xs mt-1">{t('defaultError')}</p>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12} className="!my-[15px]">
        <FormControl fullWidth>
          <div className="flex w-full">
            <TextField
              autoComplete="off"
              type="text"
              className="custom-textfield w-[95%]"
              size="small"
              placeholder={t(`onbStakeAddress2`) as string}
              error={!!errors.addressLine2}
              helperText={errors.addressLine2?.message as string}
              {...register('addressLine2', {
                maxLength: {
                  value: 100,
                  message: t('addressTooLong'),
                },
                pattern: {
                  value: /^[a-zA-Z0-9\s]+$/,
                  message: t('addressInvalidCharacters'),
                },
                validate: {
                  noLeadingSpace: (value: string): any =>
                    !value.startsWith(' ') || t('addressStartsWithSpace'),
                  noTrailingSpace: (value: string): any =>
                    !value.endsWith(' ') || t('addressEndsWithSpace'),
                  noConsecutiveSpaces: (value: string): any =>
                    !/\s\s/.test(value) || t('addressConsecutiveSpaces'),
                },
              })}
              inputProps={{
                maxLength: 100,
                pattern: '[a-zA-Z0-9\\s]+',
                onInput: (e: React.FormEvent<HTMLInputElement>) => {
                  const input = e.currentTarget;
                  input.value = input.value.replace(/[^a-zA-Z0-9\s]/g, '');
                },
              }}
            />

            {addressLine2Field?.length > 0 && !errors.addressLine2 && (
              <img src={icon_check_circle_green} alt="" className="w-[25px]" />
            )}
          </div>
        </FormControl>
      </Grid>

      <Grid container rowSpacing="auto" columnSpacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">{t(`city`)}</Typography>

          <FormControl fullWidth>
            <div className="flex w-full">
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield w-[90%]"
                size="small"
                placeholder={t('city') as string}
                {...register('city', {
                  required: true,
                  pattern: {
                    value: onlyTextRegex,
                    message: '',
                  },
                })}
              />

              {cityField?.length > 0 && !errors.city && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px]"
                />
              )}
            </div>

            {errors && errors.city?.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.city?.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">{t(`state`)}</Typography>

          <FormControl fullWidth>
            <div className="flex w-full">
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield w-[90%]"
                size="small"
                placeholder={t(`state`) as string}
                {...register('state', {
                  required: true,
                  pattern: {
                    value: onlyTextRegex,
                    message: '',
                  },
                })}
              />

              {stateField?.length > 0 && !errors.state && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px]"
                />
              )}
            </div>

            {errors && errors.state && errors.state.type === 'required' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('defaultError')}
              </p>
            )}
            {errors && errors.state && errors.state.type === 'pattern' && (
              <p className="text-orange-600 text-xs mt-1">
                {t('onbOnlyTextError')}
              </p>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6} className="!mt-[15px]">
          <Typography variant="subtitle2">{t(`postCode`)}</Typography>
          <FormControl fullWidth>
            <div className="flex w-full">
              <TextField
                autoComplete="off"
                type="number"
                className="custom-textfield w-[90%]"
                size="small"
                placeholder={t(`postCode`) as string}
                {...register('postCode', {
                  required: true,
                  pattern: {
                    value: onlyNumbersRegex,
                    message: '',
                  },
                })}
              />

              {postCodeField?.length > 0 && !errors.postCode && (
                <img
                  src={icon_check_circle_green}
                  alt=""
                  className="w-[25px]"
                />
              )}
            </div>

            {errors &&
              errors.postCode &&
              errors.postCode.type === 'required' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
            {errors &&
              errors.postCode &&
              errors.postCode.type === 'pattern' && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('onbOnlyNumberError')}
                </p>
              )}
          </FormControl>
        </Grid>
      </Grid>

      <Grid item xs={12} className="!mb-5">
        <Controller
          name="proofOfAddress"
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <FileInputV3
              {...field}
              isSingleFile
              inputName="proofOfAddress"
              onChange={changeProofOfAddressDocument}
              onDropChange={handleDropChangeFile}
              onDelete={deleteProofOfAddressDocument}
              documents={proofOfAddressDocument ?? []}
              title={t('onbStHlResidenceFile') ?? ''}
              subTitle={t('onbOldDocument') ?? ''}
              wrapperClasses="!m-0 !mb-2"
              error={
                errors.proofOfAddress
                  ? new Error(t('onbDocumentRequired') as string)
                  : undefined
              }
            />
          )}
        />
      </Grid>
    </Box>
  );
};

export default DirectionInfo;
